/// <amd-module name="Release/Landing/LeftPanel/LeftPanel" />
import * as React from "react";

import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";

import * as Utils_String from "VSS/Core/Util/String";
import { IMenuItem } from "VSSUI/Menu";
import { TabBar, TabSize, Tab } from "VSSUI/Tabs";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Intersection } from "VSSUI/Intersection";

import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { newItemParentAction } from "Release/Commands/PipelineCommandsService";
import { newFolderAction } from "Release/Commands/CreateNewFolderService";
import { userHasMemberClaim } from "Release/Helpers/Permission";

import { LeftPanePivotBarKeys, MessageBarKeys, ReleaseLandingCommandBarConstants } from "Constants";
import { PipelineSearch } from "./PipelineSearch/PipelineSearch";
import { ActivePipelines } from "./ActivePipelines/ActivePipelines";
import { AllDefinitionsTree } from "./AllPipelines/AllDefinitionsTree";
import { AllDefinitionsActionCreator } from "./AllPipelines/AllDefinitionsActionCreator";
import { PipelinesStore } from "LeftPanel/PipelinesStore";
import { PipelineSearchActionCreator } from "./PipelineSearch/PipelineSearchActionCreator";
import { SelectionStore } from "Selection/SelectionStore";
import { IDeletedPipelinesUIService } from "../ExternalUIServices/IDeletedPipelinesUIService";
import * as Resources from "Release/Landing/Resources";
import { getHubFolderCommands } from "../HubCommands";
import { MessageActions } from "Release/Landing/HubMessageBar/MessageActions";

import { IVssFeatureService } from "VSS/Platform/Feature";

interface ILeftPanelProps extends IVssComponentProperties {
    selectedPivot: string;
    onPivotChange: (pivotKey: string) => void;
}

export class LeftPanel extends VssComponent<ILeftPanelProps, {}>{
    constructor(props: ILeftPanelProps, context: IVssComponentContext) {
        super(props, context);

        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
        this._pipelineSearchActionCreator = ActionCreatorManager.GetActionCreator<PipelineSearchActionCreator>(PipelineSearchActionCreator);
    }

    public render(): React.ReactNode {
        return (<Intersection>
            <div className={"release-hub-left flex-column"}>
                <PipelineSearch
                    onSearchTextChanged={this._onSearchTextChanged}
                    placeholderText={Resources.SearchPipelinePlaceholder}
                    disabled={this.props.selectedPivot === LeftPanePivotBarKeys.Deleted}
                />
                <div className={"flex-grow flex-column left-tabs"}>
                    <TabBar
                        className={"release-left-tabbar"}
                        selectedTabId={this.props.selectedPivot}
                        onSelectedTabChanged={(id: string) => this.props.onPivotChange(id)}
                        renderAdditionalContent={this._renderTabBarCommands}
                        tabSize={TabSize.Compact}
                    >
                        <Tab
                            id={LeftPanePivotBarKeys.Active}
                            ariaLabel={Resources.ActivePipelinesPivotAriaLabel}
                            name={""}
                            iconProps={{ iconName: "CheckList" }} />
                        <Tab
                            id={LeftPanePivotBarKeys.All}
                            ariaLabel={Resources.AllPipelinesPivotAriaLabel}
                            name={""}
                            iconProps={{ iconName: "FabricFolder" }} />
                        {
                            userHasMemberClaim(this.context.pageContext)
                            && <Tab
                                id={LeftPanePivotBarKeys.Deleted}
                                ariaLabel={Resources.DeletedPipelinesTabAriaLabel}
                                name={""}
                                iconProps={{ iconName: "Delete" }}
                            />
                        }
                    </TabBar>
                    <div className={"left-tab-content flex-grow flex-column"}>
                        {
                            this._getTabContent()
                        }
                    </div>
                </div>
            </div>
        </Intersection>);
    }


    private _getTabContent(): React.ReactNode {
        switch (this.props.selectedPivot) {
            case LeftPanePivotBarKeys.Active:
                return (<div className={"absolute-fill release-hub-left-pipeline-panel"}>
                    <ActivePipelines />
                </div>);
            case LeftPanePivotBarKeys.All:
                return (<div className={"absolute-fill"}>
                    <AllDefinitionsTree />
                </div>);
            case LeftPanePivotBarKeys.Deleted:
                return this.context.pageContext.getService<IDeletedPipelinesUIService>("IDeletedPipelinesUIService").getLeftPaneUI();

        }
    }

    private _renderTabBarCommands = () => {
        if (this.props.selectedPivot !== LeftPanePivotBarKeys.Deleted) {
            const tabBarItems = this._getLeftTabBarItems(this.props.selectedPivot);
            tabBarItems.forEach(item => item.subtle = true);
            return <HeaderCommandBar items={tabBarItems} moreButtonId={ReleaseLandingCommandBarConstants.moreButtonId} />;    
        }
    };

    private _getLeftTabBarItems(pivotKey: string): IHeaderCommandBarItem[] {
        const selection = StoreManager.GetStore<SelectionStore>(SelectionStore).getState();
        const pipelineStore = StoreManager.GetStore<PipelinesStore>(PipelinesStore, undefined, this.context.pageContext);
        const newCommandButtonId: string = "new-pipeline-parent-command";

        const commandContext = {
            pageContext: this.context.pageContext,
            folderPath: this.props.selectedPivot === LeftPanePivotBarKeys.All ? pipelineStore.getFolderPathFromSelection(selection) : "",
            isFolder: !!selection.folderId,
            successCallback: (message: React.ReactNode) => this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: message }),
            errorCallback: (message: React.ReactNode) => this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: message })
        } as IReleaseDefinitionPivotCommandContext;

        const createFolderCallback = (newFolderPath: string) => {
            ActionCreatorManager.GetActionCreator<AllDefinitionsActionCreator>(AllDefinitionsActionCreator).createFolder(this.context.pageContext, newFolderPath);
        };

        const newCommand: IHeaderCommandBarItem | undefined = newItemParentAction(commandContext, newCommandButtonId);
        if (newCommand && newCommand.subMenuProps && newCommand.subMenuProps.items && Utils_String.equals(pivotKey, LeftPanePivotBarKeys.All)) {
            const folderCommand: IHeaderCommandBarItem | undefined = newFolderAction(commandContext, createFolderCallback, newCommandButtonId);

            if (folderCommand) {
                (newCommand.subMenuProps.items as IMenuItem[]).push(folderCommand);
            }
        }

        const featureService: IVssFeatureService = this.context.pageContext.getService<IVssFeatureService>("IVssFeatureService");
        const isClassicPipelineCreationdDisabled = featureService.isFeatureEnabled("ms.vss-build-web.disable-classic-release-pipeline-creation", false)

        if (newCommand && newCommand.subMenuProps && newCommand.subMenuProps.items && isClassicPipelineCreationdDisabled) {
            newCommand.subMenuProps.items = [] as IMenuItem[];

            newCommand.tooltipProps = {
                text: Resources.CreateImportReleasesDisabled
            };
            newCommand.disabled = true;    
        }

        let menuItems: IHeaderCommandBarItem[] = []
        if (newCommand) {
            menuItems.push(newCommand);
        }

        if (selection.folderId) {
            menuItems.push(...getHubFolderCommands(commandContext).value.map(m => { return { ...m, important: false } as IHeaderCommandBarItem }));
        }

        return menuItems;
    }

    private _onSearchTextChanged = (text: string): void => {
        this._pipelineSearchActionCreator.searchReleasePipelines(this.context.pageContext, text);
    }

    private _pipelineSearchActionCreator: PipelineSearchActionCreator;
    private _messageActions: MessageActions;
}
